import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface TopBarFiltersContextValue {
  filters: ReactNode;
  setFilters: (filters: ReactNode) => void;
}

const TopBarFiltersContext = createContext<TopBarFiltersContextValue | null>(null);

interface TopBarFiltersProviderProps {
  children: ReactNode;
}

/**
 * Owns the current page's filter controls so the shared console top bar can render them
 * generically: any route can call `useTopBarFilters` with its own filter UI, and the top
 * bar displays whatever the currently mounted page registered without knowing about it.
 */
export function TopBarFiltersProvider({ children }: TopBarFiltersProviderProps) {
  const [filters, setFilters] = useState<ReactNode>(null);

  const value = useMemo<TopBarFiltersContextValue>(() => ({ filters, setFilters }), [filters]);

  return <TopBarFiltersContext.Provider value={value}>{children}</TopBarFiltersContext.Provider>;
}

function useTopBarFiltersContext(): TopBarFiltersContextValue {
  const value = useContext(TopBarFiltersContext);

  if (!value) {
    throw new Error("useTopBarFiltersContext must be used within a TopBarFiltersProvider.");
  }

  return value;
}

/** Reads the currently registered top bar filter content. Used by the shared layout only. */
export function useTopBarFiltersSlot(): ReactNode {
  return useTopBarFiltersContext().filters;
}

/**
 * Registers `filters` as the content rendered in the shared console top bar for as long as
 * the calling page is mounted. Pass `null` (or omit) to render no filters. Each page owns
 * its own filter component and state; the top bar just displays whatever is registered.
 */
export function useTopBarFilters(filters: ReactNode): void {
  const { setFilters } = useTopBarFiltersContext();

  useLayoutEffect(() => {
    setFilters(filters);
    return () => setFilters(null);
  }, [filters, setFilters]);
}
